import { useEffect, useState, createContext, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React from 'react';

import { JWButton } from '../brand';
import { RootState } from '../../store';
import notificationsSlice from '../../store/slices/notifications';
import { AvatarImage } from './AvatarImage';
import logo from '@/assets/imgs/JamWithLogo.webp';
import logoNight from '@/assets/imgs/JamWithLogoNight.webp';
import { useNightMode } from '@/theme/useNightMode';
import { getReferralLink } from '../../api/user';
import CopyLinkModal from '../modals/CopyLinkModal';
import PaymentSelectModal from '../modals/PaymentSelectModal';
import {
    LightningIcon,
    MonthChevron,
    NotificationBell,
    NotificationBellBlack,
} from '../icons';
import { boostProfile } from '../../utils/paymentUtil';
import { NotificationListProps } from '@/types/notification';
import HeaderDropdown from './HeaderDropdown';
import SocialPeopleIcon from '../icons/SocialPeopleSVG';
import { SeparatorVertical } from 'lucide-react';
import SearchInput from './SearchInput';
import { getProfileFromUser } from '@/api/profile';
import { authHref } from '@/utils/authReturn';
import { HEADER_LIGHT_ACTION_CLASS, HEADER_SEARCH_SHELL_CLASS } from '@/theme/surfaceClasses';

import './Header.css';

// Create a global context for dialog state
export const DialogContext = createContext({
    isDialogOpen: false,
    setIsDialogOpen: (open: boolean) => {}
});

const NotificationList = lazy(() => import('./NotificationHub/NotificationList'));

const HeaderNotificationList = (props: NotificationListProps) => (
    <Suspense fallback={null}>
        <NotificationList {...props} />
    </Suspense>
);

const Header = () => {
    const [showReferralModal, setShowReferralModal] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationHubReady, setNotificationHubReady] = useState(false);
    const [referralLink, setReferralLink] = useState<string | null | undefined>(null);
    const [query, setQuery] = useState<string>('');
    const [lastNotification, setLastNotification] = useState('');
    // Add state to track when any dialog is open
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    // Add state for directly loaded profile photo
    const [userProfilePhoto, setUserProfilePhoto] = useState<string | undefined>(undefined);

    const navigate = useNavigate();
    const { pathname, search: returnSearch, hash } = useLocation();
    const returnTo = `${pathname}${returnSearch}${hash}`;
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user);
    const profile = useSelector((state: RootState) => state.auth.profile);
    const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);

    // Fetch user profile photo directly with caching
    useEffect(() => {
        const fetchUserProfilePhoto = async () => {
            if (!user?._id) return;

            // Check if we have a cached photo URL and when it was last fetched
            const cachedPhotoData = localStorage.getItem(`userProfilePhoto_${user._id}`);

            if (cachedPhotoData) {
                try {
                    const { photoUrl, timestamp } = JSON.parse(cachedPhotoData);

                    // Only use cached photo if it's not null
                    if (photoUrl) {
                        setUserProfilePhoto(photoUrl);
                    } else {
                        // If cached photo is null, ensure we clear the state
                        setUserProfilePhoto(undefined);
                    }

                    // Check if cache is older than 1 hour
                    const ONE_HOUR = 60 * 60 * 1000; // ms
                    if (Date.now() - timestamp < ONE_HOUR) {
                        // Even with a recent cache, always fetch in the background
                        // to ensure the header always shows the profile photo
                    }
                } catch (error) {
                    console.error('Error parsing cached profile photo data:', error);
                    // Clear potentially corrupted cache
                    localStorage.removeItem(`userProfilePhoto_${user._id}`);
                }
            }

            // Always fetch fresh data to ensure header photo is updated
            try {
                const profileData = await getProfileFromUser(user._id);
                // Only update if we have valid profile data
                if (profileData) {
                    // Handle null or undefined profile photo - convert to undefined for the state
                    const photoUrl = typeof profileData.profilePhoto === 'string' ? profileData.profilePhoto : undefined;
                    setUserProfilePhoto(photoUrl);

                    // Cache the result with timestamp (even if profilePhoto is null)
                    localStorage.setItem(
                        `userProfilePhoto_${user._id}`,
                        JSON.stringify({
                            photoUrl: profileData.profilePhoto || null,
                            timestamp: Date.now()
                        })
                    );
                }
            } catch (error) {
                console.error('Error fetching user profile photo:', error);
            }
        };

        fetchUserProfilePhoto();
    }, [user?._id]);

    // Update cache when Redux profile changes (like after an upload)
    useEffect(() => {
        if (user?._id) {
            // Get photo value, ensuring it's string or undefined
            const photoUrl = typeof profile?.profilePhoto === 'string' ? profile.profilePhoto : undefined;

            // Store the updated photo in cache
            localStorage.setItem(
                `userProfilePhoto_${user._id}`,
                JSON.stringify({
                    photoUrl: profile?.profilePhoto || null,
                    timestamp: Date.now()
                })
            );

            // Also update local state
            setUserProfilePhoto(photoUrl);
        }
    }, [profile?.profilePhoto, user?._id]);

    const [renderPaymentSelection, setRenderPaymentSelection] = useState(false);
    const handleBoostProfile = async () => {
        setRenderPaymentSelection(true);
    };

    const handlePaymentSelect = async (paymentType: string) => {
        await boostProfile(paymentType);
        setRenderPaymentSelection(false);
    };
    const referAFriend = async () => {
        const referralLink = await getReferralLink();
        setReferralLink(referralLink);
        setShowReferralModal(true);
    };

    const handleCreate = () => {
        navigate('/create-jam-session');
    };

    const search = () => {
        try {
            if (!query) return;
            navigate(`/search?query=${query}`);
        } catch (e) {
            console.error(e);
        }
    };

    const onSearchTextChange = (e: any) => {
        const query = e.target.value;
        setQuery(query);
    };

    const onSearchKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            search();
        }
    };

    const toggleNotification = async () => {
        setNotificationHubReady(true);
        setShowNotification((prev) => !prev);
        // Notifications will be marked as read in the NotificationList component
    };

    // Load the hub after first paint (or immediately if the user opens it) so the
    // unread badge can populate without keeping NotificationList in the entry chunk.
    // Guests have no bell, so the hub stays unloaded.
    useEffect(() => {
        if (!user || notificationHubReady) return undefined;

        const win = window as Window & {
            requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number;
            cancelIdleCallback?: (id: number) => void;
        };
        if (win.requestIdleCallback) {
            const id = win.requestIdleCallback(() => setNotificationHubReady(true), { timeout: 2000 });
            return () => win.cancelIdleCallback?.(id);
        }

        const id = window.setTimeout(() => setNotificationHubReady(true), 1);
        return () => window.clearTimeout(id);
    }, [notificationHubReady, user]);

    const handleCloseNotifications = () => {
        // Don't close notifications if a dialog is open
        if (isDialogOpen) {
            return;
        }
        setShowNotification(false);
    };

    const handleNotificationUpdate = (
        count: number,
        lastNotificationContent: string
    ) => {
        dispatch(notificationsSlice.actions.setUnreadCount(count));
        setLastNotification(lastNotificationContent);
    };

    const { night } = useNightMode();
    const logoSrc = night ? logoNight : logo;

    return (
        <DialogContext.Provider value={{ isDialogOpen, setIsDialogOpen }}>
            <div className="flex flex-1 py-2 bg-neutral-100 fixed top-0 w-full z-50 font-nunito min-w-40 header-container dark:bg-jw-chrome dark:border-b dark:border-jw-line">
                <div className={showReferralModal ? 'z-100' : 'z-10'}>
                    <CopyLinkModal
                        url={referralLink}
                        show={showReferralModal}
                        setShow={setShowReferralModal}
                        onClose={() => setShowReferralModal(false)}
                    />
                </div>
                <div className={showReferralModal ? 'z-100' : 'z-10'}>
                    <PaymentSelectModal
                        show={renderPaymentSelection}
                        setShow={setRenderPaymentSelection}
                        onPaymentSelect={handlePaymentSelect}
                        onClose={() => setRenderPaymentSelection(false)}
                    />
                </div>
                {/* Mobile Design */}
                <div className="flex lg:hidden items-center justify-between w-full">
                    <Link className="flex-shrink-0" to={'/discover'}>
                        <img
                            src={logoSrc}
                            alt="JamWith"
                            className="w-20 h-auto"
                        />
                    </Link>
                    <div className="flex items-center space-x-3 min-w-40 flex-grow justify-end">

                        {/* Bell sits on the left of search for members. Guests get search alone. */}
                        <div className="flex items-center rounded-full h-10 pl-4">
                            {user && (
                                <div
                                    className="relative flex items-center justify-center bg-purple-light rounded-l-full h-10 w-10 pl-2 pr-1 cursor-pointer shadow-sm flex-shrink-0 dark:bg-jw-surface"
                                    onClick={toggleNotification}
                                    tabIndex={0}
                                    role="button"
                                    aria-label="Toggle notifications"
                                >
                                    <NotificationBell className="w-5 h-5 text-white" />
                                    {unreadCount > 0 && (
                                        <div className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs dark:bg-jw-accent-strong">
                                            <span>{unreadCount}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className={`${HEADER_SEARCH_SHELL_CLASS} w-40 min-w-[60px]`}>
                                <SearchInput
                                    placeholder="Search"
                                    onChange={onSearchTextChange}
                                    onKeyDown={onSearchKeyDown}
                                    onSearchClick={search}
                                    className={user ? 'rounded-r-full w-full' : 'rounded-full w-full'}
                                />
                            </div>
                        </div>

                        <div className="">
                            <HeaderDropdown
                                referAFriend={referAFriend}
                            />
                        </div>
                    </div>
                    {(user || showNotification) && notificationHubReady && (
                        <HeaderNotificationList
                            isOpen={showNotification}
                            onNotificationUpdate={handleNotificationUpdate}
                            onClose={handleCloseNotifications}
                        />
                    )}
                </div>

                {/* Desktop specific content */}
                <div className="hidden lg:flex lg:flex-1 lg:items-center w-full justify-between header-content">
                    <Link className="flex-shrink-0" to={'/discover'}>
                        <img src={logoSrc} alt="JamWith" width="80" style={{ transform: 'translateX(-8px)' }} className="desktop-header-logo" />
                    </Link>
                    <div className="flex items-center ml-3 w-full h-full">
                        {user && (
                            <>
                                <div className="notification-hub-bar flex items-center justify-between bg-purple-light rounded-t-xl relative w-[300px] lg:w-96 py-2 border-b-2 border-b-gray-light h-full dark:bg-jw-surface dark:border-b-jw-line dark:text-jw-ink">
                                    <div className="flex items-center">
                                        <Link
                                            to={
                                                user?.username
                                                    ? `/${user.username}`
                                                    : '/profile'
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="w-12 h-12 ml-3">
                                                <AvatarImage
                                                    src={userProfilePhoto}
                                                    testId={'header-profile-image'}
                                                    wrapperClass="rounded-full"
                                                />
                                            </div>
                                        </Link>

                                        <SeparatorVertical className="mx-2 w-0.5 h-12 bg-white" />

                                        <div
                                            className="flex-1 cursor-pointer"
                                            onClick={toggleNotification}
                                        >
                                            {showNotification ? (
                                                <p className="text-white text-sm font-nunito transition-opacity duration-300 ease-in-out truncate max-w-[227px] overflow-hidden whitespace-nowrap">
                                                    Notifications
                                                </p>
                                            ) : (
                                                <p className="text-white text-sm font-nunito transition-opacity duration-300 ease-in-out truncate max-w-[227px] overflow-hidden whitespace-nowrap"
                                                    dangerouslySetInnerHTML={{ __html: lastNotification || 'Notifications' }} />
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        className="flex items-center mr-3 cursor-pointer h-16"
                                        onClick={toggleNotification}
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Toggle notifications"
                                    >
                                        <div className="relative mr-2">
                                            <NotificationBell className="w-7 h-7 mr-2" />
                                            {unreadCount > 0 && (
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                                                    <span>{unreadCount}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex">
                                            {showNotification ? (
                                                <MonthChevron />
                                            ) : (
                                                <div className="rotate-180">
                                                    <MonthChevron />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {notificationHubReady && (
                                        <HeaderNotificationList
                                            isOpen={showNotification}
                                            onNotificationUpdate={handleNotificationUpdate}
                                            onClose={handleCloseNotifications}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className={`flex justify-end items-center space-x-3 ${user ? 'w-[380px]' : 'shrink-0'}`}>
                        {/* Boosting and referring both need a profile and an account, so visitors never see them. */}
                        {user ? (
                            <>
                                <JWButton
                                    onClick={handleBoostProfile}
                                    className={`${HEADER_LIGHT_ACTION_CLASS} hidden xl:flex`}
                                >
                                    <LightningIcon />
                                    <span className="ml-2 text-sm whitespace-nowrap">
                                        <span className="font-bold">Boost</span> Profile
                                    </span>
                                </JWButton>

                                <JWButton
                                    onClick={referAFriend}
                                    className={`${HEADER_LIGHT_ACTION_CLASS} w-auto`}
                                >
                                    <div className="h-[21px]">
                                        <SocialPeopleIcon />
                                    </div>
                                    <span className="ml-2 text-sm whitespace-nowrap">
                                        Refer a friend
                                    </span>
                                </JWButton>
                            </>
                        ) : (
                            <JWButton
                                onClick={() => navigate(authHref('signup', returnTo))}
                                className="h-10 px-4 hover:scale-105 transition-transform font-nunito whitespace-nowrap !border-[#EF4060] !bg-[#EF4060] hover:!bg-[#EF4060] dark:!bg-jw-accent-strong dark:!border-jw-accent-strong"
                            >
                                Sign Up
                            </JWButton>
                        )}

                        <div className={`${HEADER_SEARCH_SHELL_CLASS} w-56 min-w-[200px]`}>
                            <SearchInput
                                placeholder="Search"
                                onChange={onSearchTextChange}
                                onKeyDown={onSearchKeyDown}
                                onSearchClick={search}
                                className="rounded-full"
                            />
                        </div>

                        {!user && (
                            <JWButton
                                onClick={() => navigate(authHref('login', returnTo))}
                                className="h-10 px-4 hover:scale-105 transition-transform font-nunito whitespace-nowrap"
                            >
                                Log In
                            </JWButton>
                        )}

                        <div className="pr-0">
                            <HeaderDropdown
                                referAFriend={referAFriend}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </DialogContext.Provider>
    );
};

export default Header;
